// src/components/common/vibe/RecognitionHub.js
import React, { useState, useEffect } from 'react';
import { Award, TrendingUp, Clock, ThumbsUp, Heart, PartyPopper, Zap } from 'lucide-react';
import vibeService from '../../../services/vibe.service';
import GiveKudos from './GiveKudos';

const REACTION_ICONS = {
    'thumbs_up': ThumbsUp,
    'heart': Heart,
    'celebration': PartyPopper,
    'claps': Zap
};

const RecognitionHub = ({ theme, onKudosGiven }) => {
    const [showKudosModal, setShowKudosModal] = useState(false);
    const [leaderboard, setLeaderboard] = useState([]);
    const [recentKudos, setRecentKudos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({ kudos_leaderboard_enabled: true });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load settings
            const settingsResponse = await vibeService.getVibeSettings();
            setSettings(settingsResponse.data || { kudos_leaderboard_enabled: true });

            // Load leaderboard (top 3)
            const leaderboardResponse = await vibeService.getKudosLeaderboard(3);
            setLeaderboard(leaderboardResponse.data || []);

            // Load recent kudos
            const recentResponse = await vibeService.getRecentKudos(5);
            setRecentKudos(recentResponse.data || []);

            //console.log('✅ Recognition Hub data loaded');
        } catch (error) {
            console.error('❌ Error loading Recognition Hub data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleKudosGiven = async () => {
        await loadData();
        if (onKudosGiven) onKudosGiven();
    };

    const handleReaction = async (kudosId, reactionType) => {
        try {
            await vibeService.reactToKudos(kudosId, reactionType);
            await loadData();
        } catch (error) {
            console.error('Error reacting to kudos:', error);
        }
    };

    const getLeaderboardMedal = (rank) => {
        if (rank === 1) return '🥇';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return '🏆';
    };

    return (
        <>
            {/* Give Kudos Modal */}
            <GiveKudos
                isOpen={showKudosModal}
                onClose={() => setShowKudosModal(false)}
                onKudosGiven={handleKudosGiven}
                theme={theme}
            />

            <div className="space-y-6">
                {/* Recognition Hub Header */}
                <div className={`rounded-xl p-6 border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                                <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <h3 className={`text-lg font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Recognition Hub
                            </h3>
                        </div>
                    </div>

                    {/* Give Kudos Button */}
                    <button
                        onClick={() => setShowKudosModal(true)}
                        className="w-full px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                        <Award className="w-5 h-5" />
                        Give Kudos!
                    </button>

                    <p className={`text-xs mt-3 text-center ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Recognize your team members' great work
                    </p>
                </div>

                {/* Leaderboard */}
                {settings.kudos_leaderboard_enabled && (
                    <div className={`rounded-xl p-6 border ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className={`w-5 h-5 ${
                                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                            }`} />
                            <h4 className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Most Appreciated This Month
                            </h4>
                        </div>

                        {loading ? (
                            <div className="text-center py-4">
                                <div className="inline-block w-6 h-6 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : leaderboard.length === 0 ? (
                            <p className={`text-sm text-center py-4 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                                No kudos yet this month. Be the first to recognize someone!
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {leaderboard.map((leader, index) => (
                                    <div
                                        key={leader.employee_id}
                                        className={`flex items-center gap-3 p-3 rounded-lg ${
                                            index === 0
                                                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400'
                                                : theme === 'dark'
                                                    ? 'bg-gray-750'
                                                    : 'bg-gray-50'
                                        }`}
                                    >
                                        <div className="text-2xl">
                                            {getLeaderboardMedal(leader.rank || index + 1)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`font-medium truncate ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {leader.employee_name}
                                            </p>
                                            <p className={`text-xs ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                {leader.kudos_count} {leader.kudos_count === 1 ? 'kudos' : 'kudos'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Recent Kudos Stream */}
                <div className={`rounded-xl p-6 border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="flex items-center gap-2 mb-4">
                        <Clock className={`w-5 h-5 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <h4 className={`font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Recent Kudos
                        </h4>
                    </div>

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : recentKudos.length === 0 ? (
                        <p className={`text-sm text-center py-4 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                            No recent kudos. Start recognizing your colleagues!
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {recentKudos.map((kudos) => (
                                <div
                                    key={kudos.kudos_id}
                                    className={`p-3 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-750 border-gray-700'
                                            : 'bg-gray-50 border-gray-200'
                                    }`}
                                >
                                    <div className="flex items-start gap-2 mb-2">
                                        <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                            {kudos.giver_name?.split(' ').map(n => n[0]).join('') || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                            }`}>
                                                <span className="font-semibold">{kudos.giver_name}</span>
                                                {' → '}
                                                <span className="font-semibold">{kudos.receiver_names || 'someone'}</span>
                                            </p>
                                            <p className={`text-xs mt-1 ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                {kudos.kudos_message?.substring(0, 60)}
                                                {kudos.kudos_message?.length > 60 ? '...' : ''}
                                            </p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className={`text-xs ${
                                                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                                }`}>
                                                    {new Date(kudos.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                                </span>
                                                {kudos.reaction_count > 0 && (
                                                    <span className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                                    }`}>
                                                        • {kudos.reaction_count} reactions
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quick Reactions */}
                                    <div className="flex gap-1 ml-10">
                                        {['thumbs_up', 'heart', 'celebration', 'claps'].map((type) => {
                                            const Icon = REACTION_ICONS[type];
                                            return (
                                                <button
                                                    key={type}
                                                    onClick={() => handleReaction(kudos.kudos_id, type)}
                                                    className={`p-1.5 rounded transition-colors ${
                                                        theme === 'dark'
                                                            ? 'hover:bg-gray-700'
                                                            : 'hover:bg-gray-200'
                                                    }`}
                                                    title={type.replace('_', ' ')}
                                                >
                                                    <Icon className="w-3 h-3" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default RecognitionHub;
